import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/Layout";
import { APIURL } from "../../redux/action/config";
import { useParams, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logo.png";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import "./style.css";
import dateFormat from "dateformat";
import { formatPhoneNumber } from "../../utils/phone";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

// Utility function to format phone number using libphonenumber-js
// (Imported from utils/phone.js)

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  headerContainer: {
    flexDirection: "column",
    marginBottom: 20,
  },
  headerTop: {
    backgroundColor: "#fff",
    padding: 10,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  headerTitle: {
    backgroundColor: "#ff4e00",
    padding: 10,
    color: "white",
    textAlign: "center",
  },
  logo: {
    width: 150,
  },
  titleText: {
    color: "white",
    fontSize: 18,
    fontWeight: "bold",
  },
  dateText: {
    color: "white",
    fontSize: 10,
    marginTop: 5,
  },
  contactInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
    paddingHorizontal: 10,
  },
  contactText: {
    color: "white",
    fontSize: 10,
  },
  section: {
    marginVertical: 10,
  },
  customerDetails: {
    marginBottom: 15,
  },
  heading: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#333",
  },
  text: {
    marginBottom: 3,
    fontSize: 11,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 15,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableColHeader: {
    width: "60%", // Service column
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f2f2f2",
    padding: 5,
  },

  tableColHeaderSmall: {
    width: "20%", // Quantity & Price
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f2f2f2",
    padding: 5,
  },

  tableCol: {
    width: "60%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },

  tableColSmall: {
    width: "20%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },
  tableCellHeader: {
    fontWeight: "bold",
    fontSize: 11,
  },
  tableCell: {
    fontSize: 11,
  },
  totalsSection: {
    marginTop: 10,
    alignItems: "flex-end",
  },
  totalRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  totalLabel: {
    fontWeight: "bold",
    width: 150,
    textAlign: "right",
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 100,
    textAlign: "right",
    fontSize: 11,
  },
  notesSection: {
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 10,
  },
  disclaimerSection: {
    marginTop: 15,
    borderLeftWidth: 3,
    borderLeftColor: "#ff4e00",
    backgroundColor: "#f9f9f9",
    padding: 10,
  },
  disclaimerText: {
    fontSize: 10,
    color: "#555",
  },
  receiptText: {
    color: "white",
    fontSize: 10,
    marginTop: 3,
  },
  paidStamp: {
    position: "absolute",
    top: 175,
    left: 230,
    color: "#28a745",
    borderWidth: 5,
    borderColor: "#28a745",
    fontSize: 40,
    padding: 10,
    opacity: 0.5,
    borderRadius: 8,
    transform: "rotate(-15deg)",
    zIndex: 2,
  },
});

const InvoiceDocument = ({ data, subtotal, formattedDate }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Paid Stamp */}
      {data?.isPaid && (
        <View style={styles.paidStamp}>
          <Text>PAID</Text>
        </View>
      )}

      {/* Header */}
      <View style={styles.headerContainer}>
        <View style={styles.headerTop}>
          {/* Use Image component directly if possible, or skip if SVG causes issues. 
              React-pdf Image supports minimal formats. 
              Using it tentatively. */}
          <Image src={Logo} style={styles.logo} />
        </View>
        <View style={styles.headerTitle}>
          <Text style={styles.titleText}>
            Invoice for order {data?.orderNo}
          </Text>
          {data?.isPaid && (
            <Text style={styles.receiptText}>
              Receipt No: #{data?.receiptNumber}
            </Text>
          )}
          <Text style={styles.dateText}>Date: {formattedDate}</Text>
          <View style={styles.contactInfo}>
            <Text style={styles.contactText}>Web: roadsideresponseltd.com</Text>
            <Text style={styles.contactText}>Call Us: 242-824-7623</Text>
          </View>
        </View>
      </View>

      {/* Customer Details */}
      <View style={styles.customerDetails}>
        <Text style={styles.heading}>Customer Details</Text>
        <Text style={styles.text}>{data?.name}</Text>
        <Text style={styles.text}>{data?.business}</Text>
        <Text style={styles.text}>{data?.address}</Text>
        <Text style={styles.text}>{data?.email}</Text>
        <Text style={styles.text}>
          {data?.phone ? formatPhoneNumber(data.phone) : "N/A"}
        </Text>
      </View>

      {/* Items Table */}
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <View style={styles.tableColHeader}>
            <Text style={styles.tableCellHeader}>Service</Text>
          </View>
          <View style={styles.tableColHeaderSmall}>
            <Text style={styles.tableCellHeader}>Qty</Text>
          </View>
          <View style={styles.tableColHeaderSmall}>
            <Text style={styles.tableCellHeader}>Price</Text>
          </View>
        </View>

        {data?.order?.map((item, index) => (
          <View style={styles.tableRow} key={index}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell} wrap>
                {item.item}
              </Text>
            </View>

            <View style={styles.tableColSmall}>
              <Text style={styles.tableCell}>{item.quantity}</Text>
            </View>

            <View style={styles.tableColSmall}>
              <Text style={styles.tableCell}>
                ${parseFloat(item.price).toFixed(2)}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={styles.totalsSection}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Subtotal:</Text>
          <Text style={styles.totalValue}>
            ${parseFloat(subtotal)?.toFixed(2)}
          </Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Transaction Charges:</Text>
          <Text style={styles.totalValue}>
            ${parseFloat((subtotal * data?.charges) / 100)?.toFixed(2)}
          </Text>
        </View>
        {Number(data?.tipAmount) > 0 && (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              Tip (for {data?.technician}):
            </Text>
            <Text style={styles.totalValue}>
              ${parseFloat(data?.tipAmount)?.toFixed(2)}
            </Text>
          </View>
        )}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total:</Text>
          <Text style={styles.totalValue}>
            ${parseFloat(data?.total)?.toFixed(2)}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.text}>
          Payment Method: {data?.paymentMethod === "cash" && "Cash"}
          {data?.paymentMethod === "bank-transfer" && "Bank Transfer"}
          {data?.paymentMethod === "online" && "Online (Debit/Credit Card)"}
        </Text>
        <Text style={styles.text}>Technician Assigned: {data?.technician}</Text>
      </View>

      {/* Notes */}
      {data?.notes && (
        <View style={styles.notesSection}>
          <Text style={styles.heading}>Notes</Text>
          <Text style={styles.text}>{data.notes}</Text>
        </View>
      )}

      {/* Disclaimer */}
      <View style={styles.disclaimerSection}>
        <Text style={styles.disclaimerText}>
          Cancellation policy:{"\n"}If you cancel a dispatched roadside service more than 15 minutes after dispatch but before a tow or service truck arrives (while en-route) there will be a $25 non-refundable dispatch fee.
        </Text>
      </View>
    </Page>
  </Document>
);

const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  console.log("🚀 ~ ViewInvoice ~ id:", id);
  const [InvoceData, setInvoceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const getAllOrders = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    var config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const res = await axios.get(
        `${APIURL}/api/v1/customOrder?id=${id}`,
        config,
      );
      const filterData = res?.data?.data?.customOrder?.[0];
      setInvoceData(filterData);
      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setLoading(false);
    }
  };

  const handleDuplicate = () => {
    setSelectedItem(InvoceData); // Set the current invoice data as the item to duplicate
    setShowDuplicateModal(true);
  };

  const executeDuplicate = async () => {
    navigate("/invoice", { state: selectedItem });
  };

  useEffect(() => {
    getAllOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const subtotal = InvoceData?.order?.reduce((acc, curr) => {
    return acc + curr.quantity * curr.price;
  }, 0);
  const formattedDate = dateFormat(InvoceData?.createdAt, "d mmmm yyyy H:MM");

  return (
    <React.Fragment>
      <ToastContainer autoClose={2000} />
      <Layout title="Invoice Details" page="invoices">
        <div
          className="content d-flex flex-column flex-column-fluid"
          id="kt_content"
        >
          <div id="kt_content_container" className="container-fluid">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "60vh" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="content_container">
                <div className="pay-section">
                  <PDFDownloadLink
                    document={
                      <InvoiceDocument
                        data={InvoceData}
                        subtotal={subtotal}
                        formattedDate={formattedDate}
                        // NoImage={NoImage}
                      />
                    }
                    fileName={`${InvoceData?.orderNo}-roadsideresponse.pdf`}
                  >
                    {({ blob, url, loading, error }) =>
                      loading ? (
                        <button>Loading PDF...</button>
                      ) : (
                        <button>Download PDF</button>
                      )
                    }
                  </PDFDownloadLink>
                  <button
                    onClick={handleDuplicate}
                    className="btn btn-primary ms-2"
                  >
                    Duplicate
                  </button>
                </div>
                <div className="invoice-container inovce">
                  {InvoceData?.isPaid && <div className="paid-stamp">PAID</div>}
                  <div className="header-new">
                    <div className="header-new">
                      {/* <h1 className="roadside">Roadside</h1>
                <h1 className="response">Response</h1> */}
                      <img src={Logo} width={200} alt="Logo" />
                    </div>
                  </div>
                  <div className="header">
                    <h2>Invoice for order {InvoceData?.orderNo}</h2>
                    {InvoceData?.isPaid && (
                      <h2>Receipt No: #{InvoceData?.receiptNumber}</h2>
                    )}
                    <span
                      style={{
                        textAlign: "center",
                        color: "white",
                        fontSize: "14",
                      }}
                    >
                      Date: {formattedDate}
                    </span>
                    <div style={{ marginTop: "10px", paddingBottom: "23px" }}>
                      <span
                        style={{
                          flex: 1,
                          float: "left",
                          color: "white",
                          fontSize: "14",
                        }}
                      >
                        <a
                          href="https://roadsideresponseltd.com/"
                          style={{ color: "white", textDecoration: "none" }}
                        >
                          Web: roadsideresponseltd.com
                        </a>
                      </span>
                      <span
                        style={{
                          flex: 1,
                          float: "right",
                          color: "white",
                          fontSize: "14",
                        }}
                      >
                        Call Us: 242-824-7623
                      </span>
                    </div>
                  </div>
                  <div className="content-section">
                    <div className="billing-address">
                      <h3>Customer Details</h3>
                      <p>{InvoceData?.name}</p>
                      <p>{InvoceData?.business}</p>
                      <p>{InvoceData?.address}</p>
                      <p>{InvoceData?.email}</p>
                      <p>
                        {InvoceData?.phone
                          ? formatPhoneNumber(InvoceData.phone)
                          : "N/A"}
                      </p>
                    </div>
                    <table className="bordered-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Quantity</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {InvoceData?.order?.map((i, index) => (
                          <tr key={index}>
                            <td>{i.item}</td>
                            <td>{i.quantity}</td>
                            <td>{parseFloat(i.price)?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="note">
                      <table className="billing-section">
                        <tr>
                          <td className="label">Subtotal:</td>
                          <td>${parseFloat(subtotal)?.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td className="label m-0">
                            Transaction Charges:
                            <p className="m-0 p-0 gen-size">
                              *Note that we use a US based card processor. When paying via debit or credit card, card transaction or currency conversion fees may apply.
                            </p>
                          </td>
                          <td>
                            $
                            {parseFloat(
                              (subtotal * InvoceData?.charges) / 100,
                            )?.toFixed(2)}
                          </td>
                        </tr>
                        {Number(InvoceData?.tipAmount) > 0 && (
                          <tr>
                            <td className="label">
                              Tip (for {InvoceData?.technician}):
                            </td>
                            <td>
                              ${parseFloat(InvoceData?.tipAmount)?.toFixed(2)}
                            </td>
                          </tr>
                        )}
                        <tr className="total-hr">
                          <td className="label">Total:</td>
                          <td className="label">
                            ${parseFloat(InvoceData?.total)?.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td className="label">Payment Method:</td>
                          <td>
                            {InvoceData?.paymentMethod === "cash" && "Cash"}
                            {InvoceData?.paymentMethod === "bank-transfer" &&
                              "Bank Transfer"}
                            {InvoceData?.paymentMethod === "online" &&
                              "Online (Debit/Credit Card)"}
                          </td>
                        </tr>
                        <tr>
                          <td className="label">Technician Assigned:</td>
                          <td>{InvoceData?.technician}</td>
                        </tr>
                      </table>
                    </div>
                    <div className="billing-address">
                      {InvoceData?.paymentLink && !InvoceData?.isPaid && (
                        <div class="d-flex justify-content-end">
                          <a
                            href={InvoceData?.paymentLink}
                            target="_blank"
                            className="btn btn-primary"
                          >
                            PAY NOW
                          </a>
                        </div>
                      )}
                      <h4>Notes</h4>
                      <p>{InvoceData?.notes}</p>
                      <div style={{
                        marginTop: "15px",
                        borderLeft: "3px solid #ff4e00",
                        backgroundColor: "#f9f9f9",
                        padding: "10px",
                      }}>
                        <p style={{ margin: 0, fontSize: "13px", color: "#555", whiteSpace: "pre-line" }}>
                          {"Cancellation policy:\nIf you cancel a dispatched roadside service more than 15 minutes after dispatch but before a tow or service truck arrives (while en-route) there will be a $25 non-refundable dispatch fee."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Layout>
      <ConfirmationModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onConfirm={executeDuplicate}
        title="Duplicate Invoice"
        message="This will open the duplication form. You can review and edit before saving."
        confirmText="Yes, Duplicate"
      />
    </React.Fragment>
  );
};

export default ViewInvoice;
